import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Mail, Phone, Calendar, Clock, MapPin, MessageSquare,
  Loader2, Search, Filter, MoreVertical, Check, X, RefreshCw,
  ChevronDown, User, Building, ArrowRight, Star, Inbox, CheckCircle,
  XCircle, AlertCircle, TrendingUp
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api } from '../lib/auth';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_STYLES = {
  new: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', icon: Inbox },
  contacted: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', icon: MessageSquare },
  qualified: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30', icon: Star },
  converted: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', icon: CheckCircle },
  closed: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30', icon: XCircle },
};

function LeadCard({ lead, onStatusChange, onDelete }) {
  const [updating, setUpdating] = useState(false);
  const status = STATUS_STYLES[lead.status] || STATUS_STYLES.new;
  const StatusIcon = status.icon;
  
  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await fetch(`${API_URL}/api/directory/leads/${lead.id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      toast.success(`Lead marked as ${newStatus}`);
      onStatusChange(lead.id, newStatus);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };
  
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await fetch(`${API_URL}/api/directory/leads/${lead.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Lead deleted');
      onDelete(lead.id);
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "bg-card rounded-xl border p-4 hover:border-cyan-500/30 transition-all",
        status.border
      )}
      data-testid={`lead-card-${lead.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={cn("w-10 h-10 rounded-full flex items-center justify-center", status.bg)}>
            <User className={cn("w-5 h-5", status.text)} />
          </div>
          <div>
            <h3 className="font-semibold text-white">
              {lead.first_name} {lead.last_name}
            </h3>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Building className="w-3 h-3" />
              {lead.business_name}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Status Badge */}
          <span className={cn("px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1", status.bg, status.text)}>
            <StatusIcon className="w-3 h-3" />
            {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
          </span>
          
          {/* Actions Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card border-white/10">
              <DropdownMenuItem onClick={() => handleStatusChange('contacted')} className="cursor-pointer">
                <MessageSquare className="w-4 h-4 mr-2 text-amber-400" />
                Mark Contacted
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('qualified')} className="cursor-pointer">
                <Star className="w-4 h-4 mr-2 text-purple-400" />
                Mark Qualified
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('converted')} className="cursor-pointer">
                <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                Mark Converted
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('closed')} className="cursor-pointer">
                <XCircle className="w-4 h-4 mr-2 text-gray-400" />
                Mark Closed
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDelete} className="cursor-pointer text-red-400">
                <X className="w-4 h-4 mr-2" />
                Delete Lead
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Contact Info */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-sm text-gray-400 hover:text-cyan-400 transition-colors">
          <Mail className="w-4 h-4" />
          <span className="truncate">{lead.email}</span>
        </a>
        {lead.phone && (
          <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-sm text-gray-400 hover:text-cyan-400 transition-colors">
            <Phone className="w-4 h-4" />
            {lead.phone}
          </a>
        )}
      </div>
      
      {/* Message */}
      {lead.comment && (
        <div className="bg-white/5 rounded-lg p-3 mb-3">
          <p className="text-sm text-gray-300 line-clamp-3">{lead.comment}</p>
        </div>
      )}
      
      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-white/5">
        <div className="flex items-center gap-3">
          {lead.best_day && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {lead.best_day}
            </span>
          )}
          {lead.best_time && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {lead.best_time}
            </span>
          )}
        </div>
        <span>{formatDate(lead.created_at)}</span>
      </div>
    </motion.div>
  );
}

export default function LeadsDashboardPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState({ total: 0, new_count: 0, contacted_count: 0 });
  
  useEffect(() => {
    loadLeads();
  }, [statusFilter]);
  
  const loadLeads = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/directory/leads?limit=100`;
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
        setStats({
          total: data.total || 0,
          new_count: data.new_count || 0,
          contacted_count: data.contacted_count || 0
        });
      }
    } catch (error) {
      console.error('Failed to load leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };
  
  const handleStatusChange = (leadId, newStatus) => {
    setLeads(leads.map(lead => 
      lead.id === leadId ? { ...lead, status: newStatus } : lead
    ));
    // Update stats
    loadLeads();
  };
  
  const handleDelete = (leadId) => {
    setLeads(leads.filter(lead => lead.id !== leadId));
    setStats(prev => ({ ...prev, total: prev.total - 1 }));
  };
  
  // Filter leads by search query
  const filteredLeads = leads.filter(lead => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      lead.first_name?.toLowerCase().includes(query) ||
      lead.last_name?.toLowerCase().includes(query) ||
      lead.email?.toLowerCase().includes(query) ||
      lead.business_name?.toLowerCase().includes(query)
    );
  });
  
  return (
    <DashboardLayout>
      <div className="p-6 pt-20 max-w-7xl mx-auto" data-testid="leads-dashboard-page">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Leads Dashboard</h1>
                  <p className="text-gray-400 text-sm">Manage contact form submissions from your business listings</p>
                </div>
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <Inbox className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-lg font-bold text-white">{stats.new_count}</p>
                  <p className="text-[10px] text-gray-500">New Leads</p>
                </div>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <MessageSquare className="w-5 h-5 text-amber-400" />
                <div>
                  <p className="text-lg font-bold text-white">{stats.contacted_count}</p>
                  <p className="text-[10px] text-gray-500">Contacted</p>
                </div>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl">
                <TrendingUp className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-lg font-bold text-white">{stats.total}</p>
                  <p className="text-[10px] text-gray-500">Total</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-3 mb-6"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search leads by name, email, or business..."
              className="pl-10 bg-white/5 border-white/10 h-11"
            />
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-white/20 h-11">
                <Filter className="w-4 h-4 mr-2" />
                {statusFilter ? statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) : 'All Status'}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card border-white/10">
              <DropdownMenuItem onClick={() => setStatusFilter('')} className="cursor-pointer">
                All Status
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('new')} className="cursor-pointer">
                <Inbox className="w-4 h-4 mr-2 text-blue-400" />
                New
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('contacted')} className="cursor-pointer">
                <MessageSquare className="w-4 h-4 mr-2 text-amber-400" />
                Contacted
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('qualified')} className="cursor-pointer">
                <Star className="w-4 h-4 mr-2 text-purple-400" />
                Qualified
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('converted')} className="cursor-pointer">
                <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                Converted
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('closed')} className="cursor-pointer">
                <XCircle className="w-4 h-4 mr-2 text-gray-400" />
                Closed
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button onClick={loadLeads} variant="outline" className="border-white/20 h-11">
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
        </motion.div>
        
        {/* Leads Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              {searchQuery || statusFilter ? 'No leads match your filters' : 'No leads yet'}
            </h3>
            <p className="text-gray-400 mb-6">
              {searchQuery || statusFilter 
                ? 'Try adjusting your search or filters'
                : 'Leads from your directory contact forms will appear here'}
            </p>
            {(searchQuery || statusFilter) && (
              <Button 
                onClick={() => { setSearchQuery(''); setStatusFilter(''); }}
                variant="outline"
                className="border-cyan-500/30 text-cyan-400"
              >
                Clear Filters
              </Button>
            )}
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {filteredLeads.map((lead, index) => (
                <motion.div
                  key={lead.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <LeadCard 
                    lead={lead} 
                    onStatusChange={handleStatusChange}
                    onDelete={handleDelete}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
        
        {/* Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 p-6 bg-gradient-to-br from-cyan-900/20 to-blue-900/20 rounded-2xl border border-cyan-500/20"
        >
          <h2 className="text-xl font-bold text-white mb-4">Lead Management Tips</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mx-auto mb-3">
                <Clock className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-semibold text-white mb-1">Respond Quickly</h3>
              <p className="text-sm text-gray-400">Leads contacted within 5 minutes are 21x more likely to convert</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="font-semibold text-white mb-1">Track Progress</h3>
              <p className="text-sm text-gray-400">Update lead status as you move them through your sales funnel</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="font-semibold text-white mb-1">Measure Results</h3>
              <p className="text-sm text-gray-400">Track your conversion rate to optimize your listing's performance</p>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
